import React from 'react';
import { Button } from './ui/button';
import { Phone, MapPin, ArrowRight, Monitor, Cpu, Shield } from 'lucide-react';
import { shopInfo } from '../data/mock';

const HeroSection = () => {
  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center bg-[#f7f5f2] pt-20">
      {/* Decorative Grid Lines */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-px h-full bg-[#2563eb]/10"></div>
        <div className="absolute top-0 left-1/2 w-px h-full bg-[#2563eb]/10"></div>
        <div className="absolute top-0 left-3/4 w-px h-full bg-[#2563eb]/10"></div>
        <div className="absolute top-1/3 left-0 w-full h-px bg-[#2563eb]/10"></div>
        <div className="absolute top-2/3 left-0 w-full h-px bg-[#2563eb]/10"></div>
      </div>

      {/* Decorative Shapes */}
      <div className="absolute top-32 right-10 w-32 h-32 rounded-full bg-[#2563eb]/10 blur-3xl"></div>
      <div className="absolute bottom-32 left-10 w-40 h-40 rounded-full bg-[#06b6d4]/15 blur-3xl"></div>
      <div className="absolute top-1/2 right-1/4 w-24 h-24 rounded-full bg-[#10b981]/10 blur-2xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-[#2563eb]/20 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              <span className="text-sm text-[#736c64]">Canon Pixma Zone • Jassur</span>
            </div>

            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1e1919] leading-tight">
                {shopInfo.name}
              </h1>
              <p className="text-xl sm:text-2xl text-[#2563eb] font-medium">
                {shopInfo.tagline}
              </p>
              <p className="text-lg text-[#736c64] max-w-xl">
                Your trusted IT support and electronics solutions provider in Jassur, Himachal Pradesh. From computer repair to custom builds, networking to security - we deliver reliable, efficient, and tailored technology solutions.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white px-8 py-6 text-base font-semibold transition-all duration-200 hover:scale-105"
                onClick={() => window.open(`tel:${shopInfo.phone}`, '_self')}
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Now
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-2 border-[#2563eb] text-[#2563eb] hover:bg-[#2563eb] hover:text-white px-8 py-6 text-base font-semibold transition-all duration-200"
                onClick={scrollToContact}
              >
                <MapPin className="w-5 h-5 mr-2" />
                Get in Touch
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="flex flex-wrap gap-8 pt-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#2563eb]">24+</div>
                <div className="text-sm text-[#736c64]">Years Experience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#2563eb]">6+</div>
                <div className="text-sm text-[#736c64]">Trusted Brands</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#2563eb]">1000+</div>
                <div className="text-sm text-[#736c64]">Happy Customers</div>
              </div>
            </div>
          </div>

          {/* Hero Image/Visual */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1571857089849-f6390447191a?w=800"
                alt="IT Solutions"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1e1919]/70 to-transparent"></div>
              
              {/* Floating Card */}
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#2563eb] flex items-center justify-center flex-shrink-0">
                    <Monitor className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1e1919]">Expert IT Support</p>
                    <p className="text-sm text-[#736c64]">Mon - Sat: {shopInfo.openingHours.weekdays}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#2563eb] ml-auto" />
                </div>
              </div>
            </div>

            {/* Feature Icons */}
            <div className="absolute -top-4 -right-4 w-16 h-16 bg-white rounded-xl shadow-lg flex items-center justify-center">
              <Cpu className="w-8 h-8 text-[#2563eb]" />
            </div>
            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-white rounded-xl shadow-lg flex items-center justify-center">
              <Shield className="w-8 h-8 text-[#10b981]" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
